/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { listConsoleNavItems } from '@aglyn/aglyn'
import { buildRoute, Route } from './route-links'

/**
 * The host dashboard's tab strip, previously copy-pasted into every
 * host-scoped page (extracted with the Marketplace tab, AGL-44). Ids are
 * stable — DashboardLayout keys the active tab on them.
 *
 * Plugin-contributed tabs (AGL-394) are spliced in from the
 * ConsoleExtension registry, so a feature plugin adds a menu item without
 * editing this file. They inherit the release-flag gating in
 * DashboardLayout via their `navTabId`. The Events tab now arrives this
 * way from the events-calendar plugin.
 */
export function hostNavTabItems(
  orgSlug: string,
  host: string,
  /**
   * The workspace's effective plugin ids (AGL-758). The registry is a
   * session-wide union across every org visited, so without this a plugin
   * enabled for one workspace keeps contributing a tab to the next.
   * Omitted only by callers with no org context.
   */
  enabledPluginIds?: readonly string[],
  /**
   * Whether the signed-in user is an ADMIN of this host (AGL-1014). The
   * Admin tab (per-site plugins, Danger zone) renders only for them — a
   * site collaborator must not see it; the page and the rules enforce the
   * same boundary for anyone who navigates by URL anyway.
   */
  options?: { hostAdmin?: boolean },
) {
  const staticTabs = [
    {
      id: 'nav-tab-dashboard',
      label: 'Dashboard',
      href: buildRoute(Route.HOST_DASHBOARD, { orgSlug, host }),
    },
    {
      id: 'nav-tab-screens',
      label: 'Pages',
      href: buildRoute(Route.HOST_SCREENS, { orgSlug, host }),
    },
    {
      id: 'nav-tab-layouts',
      label: 'Layouts',
      href: buildRoute(Route.HOST_LAYOUTS, { orgSlug, host }),
    },
    // Dedicated pages for former dashboard cards (AGL-250).
    {
      id: 'nav-tab-components',
      label: 'Components',
      href: buildRoute(Route.HOST_COMPONENTS, { orgSlug, host }),
    },
    // Templates (AGL-667) sit next to the things they produce — a template
    // is a saved starting point for a screen, component or layout.
    {
      id: 'nav-tab-templates',
      label: 'Templates',
      href: buildRoute(Route.HOST_TEMPLATES, { orgSlug, host }),
    },
    // Theme lives under Setup → Theme (AGL-114); the /theme route redirects.
    {
      id: 'nav-tab-media',
      label: 'Media',
      href: buildRoute(Route.HOST_MEDIA, { orgSlug, host }),
    },
    {
      id: 'nav-tab-content',
      label: 'Content',
      href: buildRoute(Route.HOST_CONTENT, { orgSlug, host }),
    },
    // Forms (nav + list + detail) now comes from the forms plugin's
    // ConsoleExtension, served by the generic route (AGL-395); the besigner
    // and preview routes remain app routes.
    // Inbox (nav + page) now comes from the inbox plugin's ConsoleExtension,
    // served by the generic route (AGL-395).
    // Contacts (nav + page) now comes from the contacts plugin's
    // ConsoleExtension, served by the generic route (AGL-395).
    // Site users left the dashboard for their own section (AGL-350).
    {
      id: 'nav-tab-users',
      label: 'Users',
      href: buildRoute(Route.HOST_USERS, { orgSlug, host }),
    },
    // Analytics deep dive (AGL-352); the dashboard keeps the glance card.
    {
      id: 'nav-tab-analytics',
      label: 'Analytics',
      href: buildRoute(Route.HOST_ANALYTICS, { orgSlug, host }),
    },
    // Bookings, Data (nav + page) now come from their plugins'
    // ConsoleExtensions, served by the generic route (AGL-395).
    // Products, Redirects (nav + page) now come from their plugins'
    // ConsoleExtensions, served by the generic [hostId]/[pluginSlug] route
    // (AGL-395).
    // Marketing (nav + page) now comes from the marketing plugin's
    // ConsoleExtension, served by the generic route (AGL-395).
    // Logic, Automation (nav + page) now come from their plugins'
    // ConsoleExtensions, served by the generic route (AGL-395).
    // Marketplace (nav + hub page) now comes from the marketplace plugin's
    // ConsoleExtension, served by the generic route (AGL-395); the listing
    // + publisher detail pages remain app routes.
    {
      id: 'nav-tab-setup',
      label: 'Setup',
      href: buildRoute(Route.HOST_SETUP, { orgSlug, host }),
    },
    // Host Admin area (AGL-1014): owner/admin-only — per-site plugin
    // enablement and the Danger zone moved out of Setup.
    ...(options?.hostAdmin
      ? [
          {
            id: 'nav-tab-admin',
            label: 'Admin',
            href: buildRoute(Route.HOST_ADMIN, { orgSlug, host }),
          },
        ]
      : []),
  ]

  // Plugin-contributed tabs from the ConsoleExtension registry. The href
  // is host-relative ('/events'); mount it under the active host via the
  // `[pluginSlug]` route that actually serves it, so this and the page's own
  // `activeTab` cannot drift apart (AGL-649/685). Ids fall back to the
  // navTabId so DashboardLayout's release-flag gating applies.
  const pluginTabs = listConsoleNavItems(enabledPluginIds).map((item) => ({
    id: item.navTabId ?? `nav-plugin-${item.href.replace(/[^\w]+/g, '-')}`,
    label: item.label,
    href: buildRoute(Route.HOST_PLUGIN, {
      orgSlug,
      host,
      /*
       * Straight to the hub's landing section when it has sections (AGL-2501).
       *
       * The bare href redirects, and the redirect can only be a CLIENT one —
       * the plugin registry is a client-side module-global, so no server
       * component can resolve which sections exist. Linking the tab at the
       * section the reader is going to land on anyway means the common path
       * never pays for that hop, and the redirect is left for typed and
       * bookmarked bare URLs.
       *
       * `sections[0]`, not the first VISIBLE one: release verdicts are not
       * available here, and a link into a gated section is refused by the same
       * gate that hides it, which is the correct answer rather than a leak.
       * The shell's own redirect does apply the verdict, so a reader who lands
       * on a gated first section is moved along by it.
       *
       * `resolveActiveTab` compares the first segment under the site, so the
       * tab still reads as active on every section of its own hub.
       */
      pluginSlug: [
        item.href.replace(/^\//, ''),
        ...(item.sections?.length ? [item.sections[0].id] : []),
      ].join('/'),
    }),
  }))
  if (!pluginTabs.length) return staticTabs

  // Splice plugin tabs in where Bookings/Events/Products used to sit,
  // right after Analytics.
  const anchor = staticTabs.findIndex((tab) => tab.id === 'nav-tab-analytics')
  if (anchor === -1) return [...staticTabs, ...pluginTabs]
  return [
    ...staticTabs.slice(0, anchor + 1),
    ...pluginTabs,
    ...staticTabs.slice(anchor + 1),
  ]
}

export default hostNavTabItems
